import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Dialog,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios } from "../../utils/config";
import { colors } from "..";
import CancellationReasonForm from "./cancellation-reason-form";

const STATUS_OPTIONS = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : colors.white,
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: colors.white,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxWidth: 360, mt: 0.5 } } }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function reasonMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;
  const blob = String(row.name ?? "").toLowerCase();
  return q.split(/\s+/).filter(Boolean).every((tok) => blob.includes(tok));
}

function compareReasonRows(a, b, field) {
  const va = a[field];
  const vb = b[field];

  if (field === "is_active") {
    const na = va === true ? 1 : 0;
    const nb = vb === true ? 1 : 0;
    if (na !== nb) return na - nb;
  } else {
    const sa = va == null ? "" : String(va);
    const sb = vb == null ? "" : String(vb);
    const primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
    if (primary !== 0) return primary;
  }

  return (Number(a.id) || 0) - (Number(b.id) || 0);
}

export default function ManageCancellationReasons() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogReasonId, setDialogReasonId] = useState(null);

  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const openCreate = useCallback(() => {
    setDialogReasonId(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((id) => {
    setDialogReasonId(id);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => setDialogOpen(false), []);
  const handleDialogExited = useCallback(() => setDialogReasonId(null), []);

  const loadReasons = useCallback(() => {
    setLoading(true);
    baseAxios
      .get("/cancellation-reasons", { params: { is_active: "all" } })
      .then((result) => {
        const list = result.data?.reasons;
        setReasons(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setReasons([]);
        toast.error("Could not load cancellation reasons.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, []);

  const onSaved = useCallback(() => {
    loadReasons();
    closeDialog();
  }, [loadReasons, closeDialog]);

  useEffect(() => {
    loadReasons();
  }, [loadReasons]);

  const filteredReasons = useMemo(
    () =>
      reasons.filter((row) => {
        if (!reasonMatchesSearch(row, deferredSearch)) return false;
        if (filterStatus !== "" && row.is_active !== filterStatus) return false;
        return true;
      }),
    [reasons, deferredSearch, filterStatus]
  );

  const sortedReasons = useMemo(() => {
    const copy = [...filteredReasons];
    copy.sort((a, b) => {
      const c = compareReasonRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredReasons, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);
  const getRowKey = useCallback((row) => row.id, []);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "name",
        label: "Reason",
        field: "name",
        render: (value, row) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HighlightText text={value} query={sq} />
            {row.system_key ? (
              <Chip
                size="small"
                variant="outlined"
                label="Default"
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            ) : null}
          </Box>
        ),
      },
      {
        id: "is_active",
        label: "Status",
        field: "is_active",
        minWidth: 140,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={STATUS_OPTIONS}
          />
        ),
        render: (value) => (
          <Chip
            size="small"
            variant="outlined"
            color={value ? "success" : "default"}
            label={value ? "Active" : "Inactive"}
            sx={{ fontWeight: 500, lineHeight: 1 }}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEdit(row.id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Edit
          </MuiButton>
        ),
      },
    ];
  }, [deferredSearch, filterStatus, statusFilterMenuAnchor, openEdit]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Cancellation Reasons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reasons offered when cancelling an appointment. Inactive reasons
            stay on past cancellations but are no longer offered.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search reason…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": { color: "grey.800", backgroundColor: "grey.200" },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh cancellation reasons"
            onClick={loadReasons}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openCreate}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add Reason
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedReasons}
        totalRowCount={sortedReasons.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={getRowKey}
        emptyMessage="No cancellation reasons yet."
      />

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        TransitionProps={{ onExited: handleDialogExited }}
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: "min(92vh, 900px)", overflow: "hidden" },
        }}
      >
        <CancellationReasonForm
          key={dialogReasonId == null ? "new-reason" : `reason-${dialogReasonId}`}
          reasonId={dialogReasonId}
          onSaved={onSaved}
          onRequestClose={closeDialog}
        />
      </Dialog>
    </Box>
  );
}
